import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { inr, formatLabel } from "@/lib/api";
import { Stars } from "@/components/store/ReadersWall";

export function BookCard({ book, index }) {
  const flip = index % 2 === 1;
  const fromPrice = Math.min(...[book.ebook_enabled && book.ebook_price, book.physical_enabled && book.physical_price, book.hardcover_enabled && book.hardcover_price].filter((p) => p !== false && p != null));
  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
      className={`grid items-center gap-10 md:grid-cols-12 md:gap-16 ${flip ? "md:[&>*:first-child]:order-2" : ""}`}
      data-testid={`book-card-${book.slug}`}
    >
      <Link to={`/books/${book.slug}`} className="md:col-span-5" data-testid={`book-cover-link-${book.slug}`}>
        <div className={`relative mx-auto w-[240px] sm:w-[300px] ${flip ? "md:ml-auto" : "md:mr-auto"}`}>
          <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-secondary/50 blur-2xl" />
          <img src={book.cover_url} alt={book.title} className="cover-shadow lift aspect-[2/3] w-full rounded-sm object-cover" loading="lazy" />
        </div>
      </Link>
      <div className="md:col-span-7">
        <p className="eyebrow">{book.genre || "Book"} · {book.published_year}</p>
        <h3 className="mt-3 text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
          <Link to={`/books/${book.slug}`} className="hover:text-primary transition-colors" data-testid={`book-title-link-${book.slug}`}>{book.title}</Link>
        </h3>
        {book.subtitle && <p className="mt-2 font-display text-lg italic text-muted-foreground">{book.subtitle}</p>}
        {book.rating_avg && (
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground" data-testid={`card-rating-${book.slug}`}>
            <Stars value={Math.round(book.rating_avg)} size={15} />
            <span className="font-semibold text-foreground">{book.rating_avg}</span> · {book.rating_count} {book.rating_count === 1 ? "reader" : "readers"}
          </p>
        )}
        <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-foreground/80 line-clamp-4">{book.description}</p>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {book.physical_enabled && (
            <span className="rounded-sm bg-muted px-3 py-1.5 text-xs font-semibold" data-testid={`badge-physical-${book.slug}`}>
              Paperback · {inr(book.physical_price)}
              {book.stock > 0 ? <span className="font-normal text-muted-foreground"> · {book.stock} in stock</span> : <span className="text-destructive"> · sold out</span>}
            </span>
          )}
          {book.hardcover_enabled && (
            <span className="rounded-sm bg-muted px-3 py-1.5 text-xs font-semibold" data-testid={`badge-hardcover-${book.slug}`}>
              Hardcover · {inr(book.hardcover_price)}
              {book.hardcover_stock > 0 ? <span className="font-normal text-muted-foreground"> · {book.hardcover_stock} in stock</span> : <span className="text-destructive"> · sold out</span>}
            </span>
          )}
          {book.ebook_enabled && book.has_pdf && (
            <span className="rounded-sm bg-muted px-3 py-1.5 text-xs font-semibold" data-testid={`badge-ebook-${book.slug}`}>{formatLabel("ebook")} · {inr(book.ebook_price)}</span>
          )}
        </div>
        <div className="mt-8 flex items-center gap-5">
          <Link to={`/books/${book.slug}`} className="group inline-flex items-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-[#702F2A]" data-testid={`buy-button-${book.slug}`}>
            Buy This Book <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-0.5" />
          </Link>
          {book.pages && <span className="text-sm text-muted-foreground">{book.pages} pages</span>}
        </div>
      </div>
    </motion.article>
  );
}
